import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import { ModalDialog } from './ModalDialog';

interface UseModalDialogArguments {
  onClose?: () => void;
  title: ReactNode;
  text: ReactNode;
  buttonText: ReactNode;
}

interface ModalDialogProps {
  title?: string | undefined;
  text?: string | ReactNode | undefined;
  buttonText?: string | undefined;
}

interface UseModalDialog {
  openModalDialog: () => void;
  closeModalDialog: () => void;
  Modal: React.FC<ModalDialogProps>;
}

export const useModalDialog = ({
  title,
  text,
  buttonText,
  onClose,
}: UseModalDialogArguments): UseModalDialog => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);

  return {
    openModalDialog: useCallback(() => setIsOpen(true), []),
    closeModalDialog: useCallback(() => setIsOpen(false), []),
    Modal: useMemo(
      () => (props: ModalDialogProps) =>
        (
          <ModalDialog
            isOpen={isOpen}
            onClose={handleClose}
            title={props.title ?? title}
            text={props.text ?? text}
            buttonText={props.buttonText ?? buttonText}
            variant="contained"
          />
        ),
      [buttonText, handleClose, isOpen, text, title],
    ),
  };
};
